import * as React from 'react';
import { cn } from '@/lib/utils';

type Theme = 'light' | 'dark';

function read(): Theme {
  try {
    const stored = localStorage.getItem('hourz.theme');
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    /* private mode */
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Light and dark grounds, both first-class.
 *
 * Not decoration on this surface: the diff viewer is where a reviewer spends
 * most of a review, and reading thousands of mono lines on a bone ground under
 * a bright office light and on a dark ground at night are genuinely different
 * tasks. The choice is stamped on the root element before first paint by the
 * inline script in index.html, so the console never flashes the wrong ground.
 */
export function ThemeToggle({ className }: { className?: string }) {
  const [theme, setTheme] = React.useState<Theme>(read);

  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem('hourz.theme', theme);
    } catch {
      /* private mode — the attribute still applies for this session */
    }
  }, [theme]);

  return (
    <button
      type="button"
      onClick={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))}
      aria-label={theme === 'dark' ? 'Switch to the light ground' : 'Switch to the dark ground'}
      title={theme === 'dark' ? 'Light ground' : 'Dark ground'}
      className={cn(
        'inline-flex h-7 w-7 items-center justify-center rounded-sm border border-transparent text-ink-3',
        'transition-colors hover:border-rule-strong hover:bg-sunken hover:text-ink',
        className,
      )}
    >
      {theme === 'dark' ? (
        <svg viewBox="0 0 14 14" className="h-3.5 w-3.5 fill-current" aria-hidden>
          <path d="M7 0.6v2M7 11.4v2M0.6 7h2M11.4 7h2M2.5 2.5l1.4 1.4M10.1 10.1l1.4 1.4M11.5 2.5l-1.4 1.4M3.9 10.1l-1.4 1.4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" fill="none" />
          <circle cx="7" cy="7" r="2.6" />
        </svg>
      ) : (
        <svg viewBox="0 0 14 14" className="h-3.5 w-3.5 fill-current" aria-hidden>
          <path d="M12 8.6A5.6 5.6 0 0 1 5.4 2 5.6 5.6 0 1 0 12 8.6Z" />
        </svg>
      )}
    </button>
  );
}
